import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarCheck,
  CreditCard,
  DollarSign,
  Users,
  Building2,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Receipt,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatCurrency } from '../utils/formatters';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [feeProfitData, setFeeProfitData] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [pendingVenues, setPendingVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await adminService.getDashboardStats();
      setStats(data.stats);
      setRevenueData(data.revenueTrend || []);
      setFeeProfitData(data.feeProfitTrend || []);
      setRecentUsers(data.recentUsers || []);
      setPendingVenues(data.pendingVenues || []);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-admin-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Bookings', value: stats?.totalBookings ?? 0, icon: CalendarCheck, color: 'bg-blue-500' },
    { label: 'Total Collected', value: formatCurrency(stats?.totalRevenue ?? 0), icon: DollarSign, color: 'bg-green-500' },
    { label: 'Booking Fee Revenue', value: formatCurrency(stats?.bookingFeeRevenue ?? 0), icon: Receipt, color: 'bg-indigo-500' },
    { label: 'PayMongo + VAT Cost', value: formatCurrency((stats?.payMongoFees ?? 0) + (stats?.payMongoFeeVat ?? 0)), icon: CreditCard, color: 'bg-amber-500' },
    { label: 'Gross Fee Profit', value: formatCurrency(stats?.grossFeeProfit ?? 0), icon: TrendingUp, color: 'bg-emerald-500' },
    { label: 'Active Players', value: stats?.activeUsers ?? 0, icon: Users, color: 'bg-admin-500' },
    { label: 'Total Venues', value: stats?.totalVenues ?? 0, icon: Building2, color: 'bg-cyan-500' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Dashboard</h1>
          <p className="text-sm text-[#667085] mt-1">Welcome back! Here's what's happening.</p>
        </div>
        <span className="admin-badge-info flex items-center gap-1.5">
          <TrendingUp size={14} />
          Live
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="admin-card flex items-center gap-4">
            <div className={`w-12 h-12 ${card.color} rounded-xl flex items-center justify-center`}>
              <card.icon className="text-[#1F2937]" size={22} />
            </div>
            <div>
              <p className="text-sm text-[#667085]">{card.label}</p>
              <p className="text-2xl font-bold text-[#1F2937]">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="admin-card">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4">Collections Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v) => `₱${v}`} />
              <Tooltip formatter={(value) => [formatCurrency(value), 'Total collected']} />
              <Line type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={2} dot={{ fill: '#6366f1', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-card">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4">FREAC Fee Profit</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={feeProfitData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v) => `₱${v}`} />
              <Tooltip formatter={(value) => [formatCurrency(value), 'Gross fee profit']} />
              <Bar dataKey="profit" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="admin-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[#1F2937]">Recent Users</h3>
            <Link to="/users" className="text-admin-600 hover:text-admin-700 text-sm font-medium flex items-center gap-1">
              View All <ArrowRight size={14} />
            </Link>
          </div>
          {recentUsers.length === 0 ? (
            <p className="text-sm text-[#667085] py-4 text-center">No recent users</p>
          ) : (
            <div className="space-y-3">
              {recentUsers.slice(0, 5).map((user) => (
                <div key={user.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#F3F4F6] rounded-full flex items-center justify-center text-sm font-medium text-[#667085]">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1F2937] truncate">{user.name}</p>
                    <p className="text-xs text-[#667085] truncate">{user.email}</p>
                  </div>
                  <span className={`admin-badge ${user.isActive ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="admin-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[#1F2937]">Pending Venues</h3>
            <Link to="/venues" className="text-admin-600 hover:text-admin-700 text-sm font-medium flex items-center gap-1">
              View All <ArrowRight size={14} />
            </Link>
          </div>
          {pendingVenues.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-[#667085]">
              <AlertTriangle size={32} className="mb-2" />
              <p className="text-sm">No pending venues</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingVenues.slice(0, 5).map((venue) => (
                <div key={venue.id} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#1F2937]">{venue.name}</p>
                    <p className="text-xs text-[#667085]">{venue.city} • {venue.ownerName}</p>
                  </div>
                  <span className="admin-badge-warning">Pending</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
